import { useEffect, useState } from 'react';
import { Typography, Button, InputNumber, Radio, Spin, Alert, message } from 'antd';
import { api } from '../../services/api';

const { Text } = Typography;

const pesos = (n) => (n == null ? '—' : `$ ${Number(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const num = { fontVariantNumeric: 'tabular-nums' };

// PrecioAutomatico es la parte del precio que existe solo con la capacidad de
// precios automáticos: modo, costo y márgenes del producto. En automático el
// precio de venta lo calcula el backend (costo × (1 + margen)); acá solo se
// muestra lo que va a dar. Se guarda aparte del resto del formulario porque
// recalcula al momento.
export const PrecioAutomatico = ({ productoId, primary, primaryText, onModo, onGuardado }) => {
  const [datos, setDatos] = useState(null);
  const [modo, setModo] = useState('manual');
  const [costo, setCosto] = useState(null);
  const [margenes, setMargenes] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  const aplicar = (d) => {
    setDatos(d);
    setModo(d.modo);
    setCosto(d.costo);
    setMargenes(Object.fromEntries(d.listas.map((l) => [l.id_lista, l.margen_producto])));
    onModo?.(d.modo);
  };

  useEffect(() => {
    api.admin.catalogo.getPrecioAuto(productoId).then((res) => {
      if (res.status === 'success') aplicar(res.data);
      else setError(res.message || 'No se pudo cargar el precio automático');
    }).catch(() => setError('Error de red'));
  }, [productoId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <Alert type="error" message={error} style={{ marginBottom: 16 }} />;
  if (!datos) return <div style={{ padding: 16, textAlign: 'center' }}><Spin /></div>;

  // Lo que daría con lo que está escrito, antes de guardar.
  const calcular = (l) => {
    const m = margenes[l.id_lista] ?? l.margen_tienda;
    if (costo == null || m == null) return null;
    return Math.round(costo * (1 + m / 100) * 100) / 100;
  };

  const cambios = modo !== datos.modo || costo !== datos.costo ||
    datos.listas.some((l) => (margenes[l.id_lista] ?? null) !== (l.margen_producto ?? null));

  const guardar = async () => {
    setGuardando(true);
    try {
      const res = await api.admin.catalogo.setPrecioAuto(productoId, {
        modo,
        costo: costo ?? undefined,
        margenes: Object.fromEntries(datos.listas.map((l) => [l.id_lista, margenes[l.id_lista] ?? null])),
      });
      if (res.status === 'success') {
        aplicar(res.data);
        message.success(modo === 'automatico' ? 'Precio recalculado' : 'Guardado');
        onGuardado?.();
      } else {
        message.error(res.message || 'No se pudo guardar');
      }
    } catch {
      message.error('Error de red');
    } finally {
      setGuardando(false);
    }
  };

  const auto = modo === 'automatico';
  const sinMargen = auto && datos.listas.some((l) => (margenes[l.id_lista] ?? l.margen_tienda) == null);

  return (
    <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <Radio.Group value={modo} onChange={(e) => setModo(e.target.value)} optionType="button">
          <Radio value="automatico">Automático</Radio>
          <Radio value="manual">Manual</Radio>
        </Radio.Group>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text type="secondary" style={{ fontSize: 13 }}>Costo</Text>
          <InputNumber
            min={0} step={0.01} prefix="$" value={costo} onChange={setCosto}
            placeholder="Sin cargar" style={{ width: 160 }} aria-label="Costo"
          />
        </div>
      </div>
      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 8 }}>
        {auto
          ? 'El precio de venta sale del costo más el margen de cada lista. Sin margen propio, usa el de la tienda (Márgenes).'
          : 'El precio de venta se carga a mano abajo. El costo queda guardado igual, como referencia.'}
      </Text>

      {auto && (
        <div style={{ marginTop: 12 }}>
          {datos.listas.map((l) => (
            <div key={l.id_lista} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0',
              borderTop: '1px solid #eef0f3', flexWrap: 'wrap',
            }}>
              <Text style={{ flex: '1 1 120px', fontSize: 14 }}>{l.nombre}</Text>
              <InputNumber
                value={margenes[l.id_lista]} onChange={(v) => setMargenes((m) => ({ ...m, [l.id_lista]: v }))}
                min={-99.99} step={1} suffix="%" style={{ width: 130 }}
                placeholder={l.margen_tienda != null ? `${l.margen_tienda} (tienda)` : 'Sin margen'}
                aria-label={`Margen ${l.nombre}`}
              />
              <Text strong style={{ ...num, width: 130, textAlign: 'right', fontSize: 14 }}>{pesos(calcular(l))}</Text>
            </div>
          ))}
          {sinMargen && (
            <Text type="warning" style={{ display: 'block', fontSize: 12, marginTop: 6 }}>
              Una lista sin margen (ni del producto ni de la tienda) no genera precio.
            </Text>
          )}
        </div>
      )}

      {cambios && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
          <Button type="primary" loading={guardando} onClick={guardar}
            style={{ background: primary, borderColor: primary, color: primaryText }}>
            {auto ? 'Guardar y recalcular' : 'Guardar'}
          </Button>
        </div>
      )}
    </div>
  );
};
